import React, { useState, useEffect } from 'react';
import { X, Edit, Loader2, Shield, CheckCircle, XCircle } from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@store/authStore';
import { api } from '@services/api';
import toast from 'react-hot-toast';

interface CustomFieldValue {
  customFieldId: string;
  name?: string;
  value: string[];
}

interface Contact {
  contactId: string;
  email: string;
  name?: string;
  campaignId?: string;
  tags?: string[];
  dayOfCycle?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  customFieldValues?: CustomFieldValue[];
}

interface ContactEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  dataSourceId: string;
  contact: Contact | null;
  lists: any[];
}

export const ContactEditModal: React.FC<ContactEditModalProps> = ({
  isOpen,
  onClose,
  companyId,
  dataSourceId,
  contact,
  lists,
}) => {
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [tags, setTags] = useState('');
  const [subscribed, setSubscribed] = useState(true);
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState<'' | 'male' | 'female' | 'other'>('');

  // Fetch full contact details with custom fields from GetResponse API
  const { data: fullContactData, isLoading: isLoadingContact } = useQuery({
    queryKey: ['getresponse-contact-full', companyId, dataSourceId, contact?.contactId],
    queryFn: async () => {
      if (!contact?.contactId) return null;
      const response = await api.get(`/api/v1/getresponse/contacts/${contact.contactId}`, {
        params: { companyId, dataSourceId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      console.log('🔍 [EditModal] Full contact from API:', response.data.data);
      return response.data.data;
    },
    enabled: isOpen && !!companyId && !!dataSourceId && !!contact?.contactId,
    staleTime: 0, // Always fetch fresh data
  });

  // Fetch custom fields from GetResponse to map IDs to names
  const { data: customFieldsData } = useQuery({
    queryKey: ['getresponse-custom-fields', companyId, dataSourceId],
    queryFn: async () => {
      const response = await api.get('/api/v1/getresponse/custom-fields', {
        params: { companyId, dataSourceId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return response.data.data;
    },
    enabled: isOpen && !!companyId && !!dataSourceId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Populate form when contact or custom fields change
  useEffect(() => {
    if (contact && customFieldsData) {
      setName(contact.name || '');
      // GetResponse API returns campaign as an object with campaignId property
      setCampaignId((contact as any).campaign?.campaignId || contact.campaignId || '');
      setTags(contact.tags?.join(', ') || '');
      setSubscribed(contact.dayOfCycle !== undefined);
      
      // Parse custom fields from FULL contact data (fetched from GetResponse API)
      const customFields = fullContactData?.customFieldValues || [];
      
      console.log('🔍 [EditModal] Contact data:', contact);
      console.log('🔍 [EditModal] Full contact data (with custom fields):', fullContactData);
      console.log('🔍 [EditModal] Custom fields definitions:', customFieldsData);
      console.log('🔍 [EditModal] Contact custom field values:', customFields);
      
      // Create a map of customFieldId -> field name
      const customFieldsMap = new Map(
        customFieldsData.map((field: any) => [field.customFieldId, field.name])
      );
      
      console.log('🔍 [EditModal] Custom fields map:', Object.fromEntries(customFieldsMap));
      
      // Parse customFieldValues using the map
      let phoneValue = '';
      let dobValue = '';
      let genderValue = '';
      
      customFields.forEach((cf: any) => {
        const fieldName = customFieldsMap.get(cf.customFieldId);
        if (!fieldName) return;
        
        const value = cf.value?.[0];
        if (!value) return;
        
        if (fieldName === 'phone') {
          phoneValue = value;
        } else if (fieldName === 'date_of_birth') {
          dobValue = value;
        } else if (fieldName === 'gender') {
          genderValue = value;
        }
      });
      
      setPhone(phoneValue || contact.phone || '');
      setDateOfBirth(dobValue || contact.dateOfBirth || '');
      setGender((genderValue?.toLowerCase() as any) || (contact.gender?.toLowerCase() as any) || '');
      
      console.log('🔍 [EditModal] Parsed values:', { phone: phoneValue, dob: dobValue, gender: genderValue });
    }
  }, [contact, customFieldsData, fullContactData]);

  const updateContactMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.put(`/api/v1/getresponse/contacts/${contact?.contactId}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('✅ Contact updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['getresponse-contacts'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update contact');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!contact?.contactId) {
      toast.error('Contact ID is missing');
      return;
    }

    updateContactMutation.mutate({
      companyId,
      dataSourceId,
      name: name.trim() || undefined,
      campaignId: campaignId || undefined,
      tags: tags.trim() ? tags.split(',').map(t => t.trim()) : [],
      subscribed,
      phone: phone.trim() || undefined,
      dateOfBirth: dateOfBirth || undefined,
      gender: gender || undefined,
    });
  };

  if (!isOpen || !contact) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Edit className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit Contact
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Update contact information
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={updateContactMutation.isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Email (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={contact.email}
              disabled
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Email cannot be changed
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={updateContactMutation.isPending}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1234567890"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={updateContactMutation.isPending}
            />
          </div>

          {/* DOB & Gender */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                disabled={updateContactMutation.isPending}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Gender
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value as any)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                disabled={updateContactMutation.isPending}
              >
                <option value="">Not specified</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* List */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              List <span className="text-red-500">*</span>
            </label>
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={updateContactMutation.isPending}
            >
              <option value="">Select a list...</option>
              {lists.map((list) => (
                <option key={list.campaignId} value={list.campaignId}>
                  {list.name} ({list.contactCount || 0} contacts)
                </option>
              ))}
            </select>
          </div>

          {/* Subscription Status */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={subscribed}
                onChange={(e) => setSubscribed(e.target.checked)}
                disabled={updateContactMutation.isPending}
                className="mt-1 w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    Subscribe to email campaigns
                  </span>
                  {subscribed ? (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-medium rounded-full flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Opted In
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 text-xs font-medium rounded-full flex items-center gap-1">
                      <XCircle className="w-3 h-3" />
                      Unsubscribed
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {subscribed
                    ? 'Contact will receive automated emails and campaigns'
                    : 'Contact will not receive marketing emails'}
                </p>
                <div className="flex items-center gap-1.5 mt-2 text-xs text-purple-600 dark:text-purple-400">
                  <Shield className="w-3 h-3" />
                  <span className="font-medium">GDPR Compliant</span>
                </div>
              </div>
            </label>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tags
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="customer, vip, etc. (comma separated)"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={updateContactMutation.isPending}
            />
            <p className="mt-1.5 text-xs text-yellow-600 dark:text-yellow-400 flex items-start gap-1.5">
              <span>⚠️</span>
              <span>Tags must be created in GetResponse first before assigning to contacts</span>
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={updateContactMutation.isPending}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateContactMutation.isPending}
              className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateContactMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
